import { HttpClient, HttpContext } from "@angular/common/http";
import { inject } from "@angular/core";
import { ApiError } from "aws-amplify/api";
import { catchError, map, of } from "rxjs";
import {
  TOAST_CONTEXT,
  ToastContext,
} from "src/app/core/interceptors/http-toast.interceptor";
import { environment } from "src/environments/environment";
import { SessionService } from "../../session.service";

export class BaseCrudService<TGet, TCreate, TUpdate, TParam> {
  private endpoint: string;
  protected http = inject(HttpClient);
  protected sessionService = inject(SessionService);
  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  get(params?: Record<string, any>, id?: TParam) {
    const url = this.getUrl(id);
    return this.http.get<TGet[]>(url, { params }).pipe(
      map((res: any) => (res as TGet[]) ?? []),
      catchError((error) => {
        this.handlerError(error);
        return of([] as TGet[]);
      })
    );
  }

  create(item: TCreate, toastContext?: ToastContext) {
    const url = this.getUrl();
    return this.http
      .post<TGet>(
        url,
        item,
        toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined
      )
      .pipe(
        catchError((error) => {
          this.handlerError(error);
          return of(null);
        })
      );
  }

  batchCreate(items: TCreate[], toastContext?: ToastContext) {
    const url = this.getUrl();
    return this.http
      .post<TGet>(
        url,
        items,
        toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined
      )
      .pipe(
        catchError((error) => {
          this.handlerError(error);
          return of(null);
        })
      );
  }

  update(id: TParam, item: TUpdate, toastContext?: ToastContext) {
    const url = this.getUrl(id);
    return this.http
      .put<TGet>(
        url,
        item,
        toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined
      )
      .pipe(
        catchError((error) => {
          this.handlerError(error);
          return of(null);
        })
      );
  }

  delete(
    id: TParam,
    params?: Record<string, any>,
    toastContext?: ToastContext
  ) {
    const url = this.getUrl(id);

    return this.http
      .delete<any>(url, {
        ...(toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined),
        ...(params ? { params } : {}),
      })
      .pipe(
        catchError((error) => {
          this.handlerError(error);
          return of(null);
        })
      );
  }

  private handlerError(error: any) {
    if (error instanceof ApiError) {
      if (error.response) {
        const { statusCode, body } = error.response;
        console.error(
          `Received ${statusCode} error response with payload: ${body}`
        );
      } else {
        console.error(`Received unknown error response with payload: ${error}`);
      }
    } else {
      console.error("Unexpected error: ", error);
    }
  }

  private getUrl(id?: TParam) {
    let url = environment.baseApiUrl;
    // Ensure exactly one slash between baseApiUrl and endpoint
    if (!url.endsWith("/")) url += "/";
    let endpoint = this.endpoint;
    if (endpoint.startsWith("/")) endpoint = endpoint.slice(1);
    url += endpoint;
    // Ensure exactly one slash before id if id is present
    if (id) {
      if (!url.endsWith("/")) url += "/";
      url += id;
    }
    return url;
  }
}
